import { usePageViewFeature } from "#hooks/usePageViewFeature.ts";
import { useTracking } from "#hooks/useTracking.ts";
import type { RouteHandle } from "#types/RouteHandle.ts";
import { MixpanelEvent } from "#types/tracking.ts";
import { useEffect } from "react";
import { type UIMatch, useMatches } from "react-router";

interface UsePageViewTrackingOptions {
    ignoreTracking?: boolean;
}

export const usePageViewTracking = ({ ignoreTracking } : UsePageViewTrackingOptions = {}) => {
    const { trackEvent } = useTracking();
    const matches = useMatches() as UIMatch<unknown, RouteHandle>[];
    const featureValue = usePageViewFeature();

    const currentTrackingName = [...matches]
        .reverse()
        .find(m => m.handle?.trackingName)
        ?.handle?.trackingName;

    useEffect(() => {
        if (ignoreTracking) {
            return;
        }

        // Only track if we have a page name
        //
        // Note: When navigating via query parameter (e.g., ?wai-assistant-route=/smart-reports/test/edit),
        // a pageViewed event for the Home page will be sent before the target page event because
        // the router initially loads at the default route before programmatically navigating to the target.
        if (currentTrackingName) {
            trackEvent(MixpanelEvent.PageViewed, {
                PageName: currentTrackingName,
                ...(featureValue && { Feature: featureValue })
            });
        }
    }, [currentTrackingName, trackEvent, ignoreTracking, featureValue]);
};
